<template>
    <div> 
        <index
            v-if="!showCategoryRelatedDetails"
            :compliances-data="compliancesData"
            :is-loading="isLoading"
            @add="add"
            @edit="edit"
            @upload="upload"
            @remove="remove"
            @viewRelatedDetails="viewRelatedDetails"
        />

        <view-or-create-or-edit
            ref="viewOrCreateOrEdit"
            v-model="isDataImportSideBarActive" 
            :data-import="dataToViewOrEdit"
            :is-data-import-side-bar-active="isDataImportSideBarActive"
            :is-button-action-loading="isButtonActionLoading"
            :data-status="dataStatus"
            @add="create"
            @update="update"
            @reUpload="reUpload"
        />

        <category-related-details 
            v-if="showCategoryRelatedDetails"
            ref="categoryRelatedDetails"
            :current-category="currentCategory"
        />

         <!-- delete -->
        <confirmation-modal  ref="modal">
            <template slot="body">
                <div>
                    <feather-icon class="d-block mx-auto text-warning" icon="AlertCircleIcon" size="90"/> 
                    <h1 class="text-center text-secondary display-5 mt-2">Are you sure?</h1>
                    <p class="text-center mt-2"> You won't be able to revert this! </p>
                </div>
            </template>

            <template slot="footer">
                <b-button class="d-flex" :disabled="isButtonActionLoading" size="md" variant="primary" @click="confirmDelete">
                    Yes, delete it!
                    <normal-loader v-if="isButtonActionLoading" class="left-10%" />
                </b-button>

                <b-button size="md" variant="outline-danger" @click="cancelDelete">
                    Cancel
                </b-button>
            </template>
        </confirmation-modal>
    </div>
</template>

<script>
import { BButton } from 'bootstrap-vue';
import ConfirmationModal from '@/views/components/utils/ConfirmationModal.vue';
import Compliance from '@/models/Compliance';
import { localize } from '@/localize/importListLocalizer';
import { successToast, errorToast } from '@/views/helpers/toast';
import { CREATE_SUCCESS, DELETE_SUCCESS, UPDATE_SUCCESS, RE_UPLOAD_SUCCESS } from '@/constants/success';
import { SOMETHING_WENT_WRONG } from '@/constants/errors';
import { MAS_LIST } from '@/constants/config';
import CategoryRelatedDetails from './category-related-details/CategoryRelatedDetails.vue';
import ViewOrCreateOrEdit from './ViewOrCreateOrEdit.vue';
import Index from './Index.vue';

export default {
    components : {
        BButton,

        Index,
        ViewOrCreateOrEdit,
        CategoryRelatedDetails,
        ConfirmationModal,
    },

    data() {
        return {
            isDataImportSideBarActive: false,
            isButtonActionLoading: false,
            showCategoryRelatedDetails: false,
            currentCategory:{},
            dataStatus: 'view',
            isLoading: false,
            dataToViewOrEdit: {},
            dataToDelete: null,

            compliancesData : [],
            complianceLiveData: [],
        };
    },

    watch: {
        '$route.query': {
            handler(query)
            {
                if(! query.category) {
                    this.showCategoryRelatedDetails = false;
                }
            }
        },

        isDataImportSideBarActive(active) {
            if(!active) {
                setTimeout(() => {
                    this.$refs.viewOrCreateOrEdit.files = [];
                }, 300);
            }
        }
    },

    async created() {
        this.isLoading = true;
        const data = await Compliance.all();
        this.compliancesData = data.map(compliance => localize(compliance));

        this.compliancesData.forEach(compliance => {
            if(compliance.status === MAS_LIST.STATUS_IMPORTING) {
                this.setDataLive(compliance);
            }
        });

        this.isLoading = false;
    },

    mounted() {
        if(window.location.href.includes('category')) {
            this.$router.push({name: 'compliance.mas-list'});
        }
    },

    beforeDestroy () {
        this.complianceLiveData.forEach(clearLive => {
            clearInterval(clearLive.intervalId);
        });
    },

    methods: {
        setDataLive(data) {
            const intervalId = setInterval(async() => {
                const compliance = await Compliance.getLiveStatus(data.id);

                if(compliance.status !== MAS_LIST.STATUS_IMPORTING) {
                    this.compliancesData.forEach(cd => {
                        if(cd.id === compliance.id) {
                            // eslint-disable-next-line no-param-reassign
                            cd.status = compliance.status;
                        }
                    });
                    this.clearDataLive(compliance.id);
                }
            },2000);

            this.complianceLiveData.push({'id': data.id, 'intervalId': intervalId});
        },

        clearDataLive(id) {
            this.complianceLiveData.forEach(liveData => {
                if(liveData.id === id) {
                    clearInterval(liveData.intervalId);
                }
            });
        },

        add() {
            this.dataStatus = 'create';
            this.dataToViewOrEdit = {};
            this.isDataImportSideBarActive = true;
        },

        edit(data) {
            this.dataStatus = 'edit';
            this.dataToViewOrEdit = data;
            this.isDataImportSideBarActive = true;
        },

        upload(data) {
            this.dataStatus = 'upload';
            this.dataToViewOrEdit = data;
            this.isDataImportSideBarActive = true;
        },

        remove(data) {
            this.$refs.modal.$refs.confirmationModal.show();
            this.dataToDelete = data;
        },

        viewRelatedDetails(data) {
            this.currentCategory = data;
            this.showCategoryRelatedDetails = true;
            this.$router.push({name: 'compliance.mas-list', query: { category: data.id }});
        },

        create(data) {
            this.isButtonActionLoading = true;

            const formData = this.prepareFormData(data);
            
            Compliance.create(formData)
                .then(response => {
                    const prepareResponse = localize(response);
                    this.compliancesData.push(prepareResponse);
                    this.setDataLive(prepareResponse);

                    this.isDataImportSideBarActive = false;
                    this.isButtonActionLoading = false;
                    successToast(CREATE_SUCCESS);
                })
                .catch(error => {
                    this.isButtonActionLoading = false;
                    errorToast(SOMETHING_WENT_WRONG);
                    console.log("Error wile trying to create Mas List", error);
                });
        },

        update(data) {
            this.isButtonActionLoading = true;
            Compliance.update(data.id, data)
                .then(() => {
                    this.compliancesData.forEach(item => {
                        if(item.id === data.id) {
                            Object.keys(data).forEach(key => {
                                // eslint-disable-next-line no-param-reassign
                                item[key] = data[key];
                            });
                        }
                    });

                    if(data.status !== MAS_LIST.STATUS_IMPORTED) {
                        this.setDataLive(data);
                    }

                    this.isDataImportSideBarActive = false;
                    this.isButtonActionLoading = false;
                    successToast(UPDATE_SUCCESS);
                })
                .catch(error => {
                    this.isButtonActionLoading = false;
                    errorToast(SOMETHING_WENT_WRONG);
                    console.log("Error wile trying to update Mas List", error);
                });
        },

        reUpload(data) {
            this.isButtonActionLoading = true;

            const formData = this.prepareFormData(data);
            
            Compliance.reUpload(data.id, formData)
                .then(() => {
                    // eslint-disable-next-line no-param-reassign
                    data.status = MAS_LIST.STATUS_IMPORTING;

                    this.compliancesData.forEach(item => {
                        if(item.id === data.id) {
                            Object.keys(data).forEach(key => {
                                // eslint-disable-next-line no-param-reassign
                                item[key] = data[key];
                            });
                        }
                    });
                    
                    this.setDataLive(data);

                    this.isDataImportSideBarActive = false;
                    this.isButtonActionLoading = false;
                    successToast(RE_UPLOAD_SUCCESS);
                })
                .catch(error => {
                    this.isButtonActionLoading = false;
                    errorToast(SOMETHING_WENT_WRONG);
                    console.log("Error wile trying to re-upload Mas List", error);
                });
        },

        cancelDelete() {
            this.dataToDelete = null;
            this.$refs.modal.$refs.confirmationModal.hide();
        },

        confirmDelete() {
            this.isButtonActionLoading = true;

            Compliance.delete(this.dataToDelete.id)
                .then(() => {
                    if(this.dataToDelete.status === MAS_LIST.STATUS_IMPORTING) {
                        this.clearDataLive(this.dataToDelete.id);
                    }

                    this.compliancesData = this.compliancesData.filter(item => this.dataToDelete.id !== item.id);
                    this.$refs.modal.$refs.confirmationModal.hide();

                    this.dataToDelete = null;
                    this.isButtonActionLoading = false;
                    successToast(DELETE_SUCCESS);
                })
                .catch( error => {
                    this.isButtonActionLoading = false;
                    errorToast(SOMETHING_WENT_WRONG);
                    console.log(error);
                });
        },

        prepareFormData(data) {
            const formData = new FormData();
           
            for (
                let i = 0, len = data.files.length;
                i < len;
            // eslint-disable-next-line no-plusplus
                i++
            ) {
                formData.append("files[]", data.files[i].file);
            }

            for (
                let i = 0, len = data.regulations.length;
                i < len;
            // eslint-disable-next-line no-plusplus
                i++
            ) {
                formData.append("regulations[]", data.regulations[i]);
            }

            // formData.append("regulations", data.regulations);
            formData.append("category", data.category);
            formData.append("lastUpdated", data.lastUpdated);

            return formData;
        }
    },
};
</script>
